import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import RoleListData from './roleListData';
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 500, description: 800
};

const RolesList = React.memo(({ scrollRef, filteredRolesCount, setRolesFetched, roles, setOffset, sortBy, setSortBy }) => {

    const navigate = useNavigate();

    const [roleColumnWidth, setRoleColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('roleColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setRoleColumnWidth({ ...roleColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('roleColumnWidth', JSON.stringify(roleColumnWidth));
    }, [roleColumnWidth]);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredRolesCount, setRolesFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setRolesFetched, filteredRolesCount, 70, 280);

    const handleRoleClick = useCallback((id) => {
        navigate(`rôle_details/${id}`);
    }, [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const Row = ({ index, style, data }) => {
        const { roles, handleRoleClick } = data;
        const role = index === 0 ? roles[0] : roles[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <RoleListData
                    role={role}
                    columnWidth={roleColumnWidth}
                    handleRoleClick={handleRoleClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Nom'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={roleColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Description'
                    columnKey='description'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={roleColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="50px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredRolesCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredRolesCount}
                    itemSize={70}
                    itemData={{ roles, handleRoleClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default RolesList;